import React, { Fragment, useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Button } from 'antd'
import { HStack, Icon, Link, Menu, Table, T } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { SettlementFragment, SettlementPaymentTransferFragment } from 'types/graphql'

import {
  useGetInvoicesForSettlementQuery,
  useGetReceiptsForSettlementQuery,
} from 'src/fetch/settlements'
import useDisclosure from 'src/hooks/use-disclosure'
import { DateFormats, formatDateInTimeZone } from 'src/utils/date'
import { downloadZip } from 'src/utils/download'
import { formatFee, formatPaymentMethod } from 'src/utils/fee'
import { getUrlForRecord } from 'src/utils/records'

import SettlementTransactionExpandedFeesTable from './SettlementTransactionExpandedFeesTable'

export type SettlementTransactionRecord = {
  id: number
  label: string
  link: string
  isTest: boolean
}

type Props = {
  settlement: SettlementFragment
}

const SettlementTransactionsTable = ({ settlement }: Props) => {
  const timeZone = settlement.organization?.timeZone
  const paymentProviderFormatted = useMemo(
    () => settlement.organization?.paymentProviderFormatted ?? '',
    [settlement]
  )

  const paymentTransfers = useMemo(() => settlement.paymentTransfers ?? [], [settlement])
  const settlementId = settlement.id

  const columnHelper = useMemo(() => createColumnHelper<SettlementPaymentTransferFragment>(), [])

  const recordsHeader = useMemo(() => {
    const hasMultiple = paymentTransfers.some(
      (paymentTransfer) => paymentTransfer?.records?.length !== 1
    )
    return hasMultiple ? 'Records' : 'Record'
  }, [paymentTransfers])

  const renderRecords = useCallback((paymentTransfer: SettlementPaymentTransferFragment) => {
    const records = paymentTransfer.records
    if (!records?.length) {
      return <T.Body>-</T.Body>
    }
    return (
      <T.Body>
        {records.map((record, index) => (
          <Fragment key={record.id}>
            <Link
              externalHref={getUrlForRecord({
                currentUserHasAccess: record.currentUserHasAccess ?? false,
                isDraft: record.isDraft ?? false,
                organizationSlug: record.organization?.slug ?? '',
                recordUuid: record.uuid ?? '',
              })}
            >
              <span>{record.displayIdentifier}</span>
            </Link>
            {index < records.length - 1 ? <span>, </span> : null}
          </Fragment>
        ))}
      </T.Body>
    )
  }, [])

  const columns = useMemo(() => {
    return [
      columnHelper.accessor((row) => row.finixId, {
        id: 'finixId',
        header: 'Finix Transaction ID',
        cell: (info) => <T.Body>{info.getValue() || '—'}</T.Body>,
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.createdAt ?? null, {
        id: 'date',
        header: 'Date',
        cell: (info) => {
          const value = info.getValue()
          if (!value) {
            return <T.Body>-</T.Body>
          }
          return (
            <T.Body className="whitespace-nowrap">
              {formatDateInTimeZone(value, timeZone ?? undefined, DateFormats.MonthNameDateTime)}
            </T.Body>
          )
        },
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.revenueCents, {
        id: 'amount',
        header: 'Revenue',
        cell: (info) => {
          const value = info.getValue()
          if (typeof value !== 'number') {
            return <T.Body>-</T.Body>
          }
          return <T.Body>{formatFee(value)}</T.Body>
        },
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.serviceFeeCents, {
        id: 'serviceFee',
        header: 'Service Fee',
        cell: (info) => {
          const value = info.getValue()
          if (typeof value !== 'number') {
            return <T.Body>-</T.Body>
          }
          return <T.Body>{formatFee(value)}</T.Body>
        },
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.paymentMethod ?? null, {
        id: 'paymentMethod',
        header: 'Payment Method',
        cell: ({ row: { original } }) => {
          if (!original.paymentMethod) {
            return <T.Body>-</T.Body>
          }
          return (
            <T.Body>{formatPaymentMethod(original.paymentMethod, paymentProviderFormatted)}</T.Body>
          )
        },
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row, {
        id: 'records',
        header: recordsHeader,
        cell: (info) => renderRecords(info.getValue()),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row, {
        id: 'glCodes',
        header: 'GL Codes',
        cell: (info) => {
          const paymentTransfer = info.getValue()
          const isRefund = paymentTransfer.isRefund
          if (isRefund) {
            return null
          }
          const glCodes = paymentTransfer.generalLedgerNumbers
            ?.map((glNumber) => glNumber.glNumber)
            .join(', ')
          return <T.Body>{glCodes}</T.Body>
        },
        enableSorting: false,
      }),
    ]
  }, [columnHelper, paymentProviderFormatted, recordsHeader, renderRecords, timeZone])

  const getRowId = useCallback((row: SettlementPaymentTransferFragment) => `${row.id}`, [])

  const getRtfsForPaymentTransfer = useCallback(
    (paymentTransfer: SettlementPaymentTransferFragment) => {
      return (paymentTransfer.recordTaskPayments ?? []).flatMap((rtp) => rtp.recordTaskFees ?? [])
    },
    []
  )

  const getRowCanExpand = useCallback(
    (row: SettlementPaymentTransferFragment) => {
      const allRtfs = getRtfsForPaymentTransfer(row)
      return !!allRtfs.length
    },
    [getRtfsForPaymentTransfer]
  )

  const getRowExpandedContent = useCallback(
    (row: SettlementPaymentTransferFragment) => {
      const allRtfs = getRtfsForPaymentTransfer(row)
      if (!allRtfs.length) {
        return null
      }

      return <SettlementTransactionExpandedFeesTable fees={allRtfs} />
    },
    [getRtfsForPaymentTransfer]
  )

  const { refetch: fetchInvoices } = useGetInvoicesForSettlementQuery(settlementId)
  const { refetch: fetchReceipts } = useGetReceiptsForSettlementQuery(settlementId)
  const { isOpen: isMenuOpen, open: openMenu, close: closeMenu } = useDisclosure()

  const onDownloadInvoices = useCallback(async () => {
    const result = await fetchInvoices()
    const files = result?.data?.getInvoicesForSettlement ?? []
    await downloadZip({
      files: files.map((file) => ({
        url: file?.url?.signedUrl ?? '',
        downloadableName: file?.name ?? '',
      })),
      zipFileName: 'invoices.zip',
    })
  }, [fetchInvoices])

  const onDownloadReceipts = useCallback(async () => {
    const result = await fetchReceipts()
    const files = result?.data?.getReceiptsForSettlement ?? []
    await downloadZip({
      files: files.map((file) => ({
        url: file?.url?.signedUrl ?? '',
        downloadableName: file?.name ?? '',
      })),
      zipFileName: 'receipts.zip',
    })
  }, [fetchReceipts])

  const tableActions = useMemo(() => {
    return (
      <HStack gap={2} justify="end">
        <Menu isOpen={isMenuOpen} onClose={closeMenu} onOpen={openMenu}>
          <Menu.Trigger>
            <div>
              <Button icon={<Icon.FaDownload />}>Download</Button>
            </div>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Item onClick={onDownloadInvoices}>
              <T.Body className="text-right">Invoices</T.Body>
            </Menu.Item>
            <Menu.Item onClick={onDownloadReceipts}>
              <T.Body className="text-right">Receipts</T.Body>
            </Menu.Item>
          </Menu.Content>
        </Menu>
      </HStack>
    )
  }, [isMenuOpen, closeMenu, openMenu, onDownloadInvoices, onDownloadReceipts])

  const enableRowExpansion = useMemo(() => {
    return paymentTransfers.some(
      (paymentTransfer) => getRtfsForPaymentTransfer(paymentTransfer).length
    )
  }, [paymentTransfers, getRtfsForPaymentTransfer])

  const tableProps = useTableState<SettlementPaymentTransferFragment>()

  return (
    <Table
      actions={tableActions}
      columns={columns}
      data={paymentTransfers}
      title="Transaction Details"
      showCount
      totalCount={paymentTransfers.length}
      emptyStateMessage={
        paymentTransfers?.length ? undefined : 'There are no transactions for this settlement.'
      }
      enablePagination={false}
      enableRowExpansion={enableRowExpansion}
      getRowCanExpand={getRowCanExpand}
      getRowExpandedContent={getRowExpandedContent}
      getRowId={getRowId}
      {...tableProps}
    />
  )
}

export default React.memo(SettlementTransactionsTable)
